/**
 * cart-promo-storefront.js
 *
 * Shopify Storefront API + /products/{handle}.js 호출 모듈 (Phase 4a, 2026-05-27).
 *
 * 책임: BXGY 카탈로그 candidate 의 storefront 재고·available 검증.
 *   - /products/{handle}.js (storefront product JSON) 으로 variant.available 검증
 *   - Storefront GraphQL `quantityAvailable` 로 BE stock 정량 보정
 *
 * 상태 모델: instance state (cache, token, domain) 는 hub 가 보유. 본 모듈 함수는 매개변수로 받음.
 * → 순수 함수 보장 + 호출부 무수정 (hub wrapper 가 instance state 매개로 전달).
 *
 * 의존:
 *   - window.CartCouponHubUtils (productHandleFromUrl, toShopifyGid, matchStorefrontVariant, resolveVariantStock)
 *
 * 로드 순서 (sections/main-cart.liquid + snippets/cart-drawer.liquid):
 *   1. cart-coupon-utils.js
 *   2. cart-promo-effects.js
 *   3. cart-promo-storefront.js  ← 본 파일
 *   4. cart-coupon-hub.js
 *
 * 회귀 박제:
 *   - BXGY selectable 솔드아웃 FE 디펜시브 (2026-05-26) — wiki/docs/bxgy-selectable-sold-out-fe-defense
 *   - Storefront quantityAvailable SSOT (2026-05-27) — BE catalog stock stale 보정
 *   - 404 / handle mismatch graceful fallback — 전체 후보 탈락 회피
 */
(function (global) {
  'use strict';

  const Utils = global.CartCouponHubUtils;
  if (!Utils) {
    console.warn('[cart-promo-storefront] CartCouponHubUtils 미로드 — 로드 순서 확인');
  }

  const Storefront = {

    // handle 단위 grouping + cache 활용. cache 매개변수 미전달 시 cache miss/no-store.
    async fetchProduct(handle, cache) {
      if (cache && Object.prototype.hasOwnProperty.call(cache, handle)) {
        return cache[handle];
      }
      try {
        const res = await fetch(MISTO.localeUrl(`/products/${encodeURIComponent(handle)}.js`), {
          credentials: 'same-origin',
        });
        if (!res.ok) throw new Error(`product ${handle} ${res.status}`);
        const product = await res.json();
        if (cache) cache[handle] = product;
        return product;
      } catch (err) {
        console.warn('[cart-promo-storefront] product unavailable', handle, err);
        if (cache) cache[handle] = null;
        return null;
      }
    },

    // 🔴 Storefront GraphQL 로 variant.quantityAvailable 받아 BE stock 보정 (2026-05-27).
    //   BE catalog `stock` 은 product_read_cache 기반 → stale / 과장 가능 (verified: 00L BE=4 vs 실제 1).
    //   Storefront API quantityAvailable 은 EC 채널 단독값 (HK_EC 만 EC 연결 정책) → admin HK_EC available 과 일치.
    //   token 미설정 시 빈 Map 반환 (호출자가 BE stock 단독 신뢰).
    //   availableForSale=false → null 반환 (hide, /products/{handle}.js available 검증 백업).
    //   quantityAvailable=null (예: inventory_policy='continue') → BE stock 유지.
    //   회귀 case: docs/policy/cart-coupon-hub.md §변경 이력 (2026-05-27 storefront 정량 가드).
    async fetchInventory(variantGids, token, domain) {
      if (!token || !domain) return new Map();
      const uniqueIds = Array.from(new Set((variantGids || []).filter(Boolean)));
      if (!uniqueIds.length) return new Map();
      try {
        const res = await fetch(`https://${domain}/api/2024-10/graphql.json`, {
          method: 'POST',
          credentials: 'omit',
          headers: {
            'X-Shopify-Storefront-Access-Token': token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query: 'query Inv($ids:[ID!]!){nodes(ids:$ids){... on ProductVariant{id availableForSale quantityAvailable}}}',
            variables: { ids: uniqueIds },
          }),
        });
        if (!res.ok) {
          console.warn('[cart-promo-storefront] inventory', res.status);
          return new Map();
        }
        const payload = await res.json();
        const map = new Map();
        (payload?.data?.nodes || []).forEach((n) => {
          if (n?.id) map.set(n.id, { availableForSale: n.availableForSale, quantityAvailable: n.quantityAvailable });
        });
        return map;
      } catch (err) {
        console.warn('[cart-promo-storefront] inventory err', err);
        return new Map();
      }
    },

    // BXGY catalog item → storefront product 매칭. handle → fetchProduct → matchStorefrontVariant.
    // available=false 또는 variant 미발견 → null (해당 item 제외).
    async resolveProduct(item, cache) {
      const handle = item.handle || Utils.productHandleFromUrl(item.url);
      if (!handle) return null;
      const product = await Storefront.fetchProduct(handle, cache);
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      if (!variants.length) return null;
      const variant = Utils.matchStorefrontVariant(item, variants);
      if (!variant || variant.available === false) return null;
      const variantId = String(variant.id || '');
      if (!variantId) return null;
      return {
        ...item,
        variantId,
        variantGid: Utils.toShopifyGid('ProductVariant', variantId),
        id: `catalog:${Utils.toShopifyGid('ProductVariant', variantId)}:${handle}`,
        option: item.option || variant.title || '',
        sku: item.sku || variant.sku || '',
        barcode: item.barcode || variant.barcode || '',
        stock: Utils.resolveVariantStock(variant),
      };
    },

    async resolveProducts(items, cache) {
      const resolved = await Promise.all((items || []).map((item) => Storefront.resolveProduct(item, cache)));
      return resolved.filter(Boolean);
    },

    // 🔴 BXGY candidate 솔드아웃 FE 디펜시브 (2026-05-27).
    //   BE response 의 stock 부정합 (예약 hold 미반영 / cache stale) 으로 판매가능=0 상품이
    //   candidate 노출 → cart/add 422 회귀. /products/{handle}.js 의 variant.available 로 재검증.
    //   매칭 실패 / 404 (handle 불일치) → graceful fallback: BE stock 신뢰 (전체 후보 탈락 회피).
    //   stock 보정은 applyStockCorrection(GraphQL) 에서. 본 함수는 available 필터만.
    async verifyAvailability(candidates, cache) {
      const handles = Array.from(new Set((candidates || []).map((c) => c.handle).filter(Boolean)));
      const productByHandle = new Map();
      await Promise.all(handles.map(async (handle) => {
        try {
          const product = await Storefront.fetchProduct(handle, cache);
          if (product) productByHandle.set(handle, product);
        } catch (_err) {
          // 404 등 → graceful fallback (productByHandle 에 안 넣음)
        }
      }));
      return (candidates || [])
        .map((c) => {
          if (!c.handle) return c;
          const product = productByHandle.get(c.handle);
          if (!product) return c;
          const variants = Array.isArray(product.variants) ? product.variants : [];
          const variant = Utils.matchStorefrontVariant(c, variants);
          if (!variant) return c;
          if (variant.available === false) return null;
          return c;
        })
        .filter(Boolean);
    },

    // Storefront GraphQL quantityAvailable → candidate stock min 보정.
    //   token 미설정 / fetch 실패 → 보정 skip (BE stock 단독 신뢰).
    //   availableForSale=false → null (hide).
    //   quantityAvailable=null → BE stock 유지.
    async applyStockCorrection(candidates, token, domain) {
      if (!token) return candidates;
      const variantGids = (candidates || []).map((c) => c.variantGid).filter(Boolean);
      if (!variantGids.length) return candidates;
      const inv = await Storefront.fetchInventory(variantGids, token, domain);
      if (inv.size === 0) return candidates;
      return (candidates || [])
        .map((c) => {
          const info = inv.get(c.variantGid);
          if (!info) return c;
          if (info.availableForSale === false) return null;
          if (info.quantityAvailable == null) return c;
          const beStock = Number.isFinite(c.stock) ? c.stock : Infinity;
          return { ...c, stock: Math.min(beStock, info.quantityAvailable) };
        })
        .filter(Boolean);
    },
  };

  global.CartPromoStorefront = Storefront;
})(typeof window !== 'undefined' ? window : globalThis);
